import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import {
  Button, Card, EmptyState, KpiCard, Note, SkeletonPage, Sym, usePageTitle,
} from '../../components/app2';
import SyncStatusCard from '../payroll-processing/components/SyncStatusCard';
import EstadoSync, { esVivo } from './components/EstadoSync';
import {
  duracion, fechaHora, hora, ORIGEN, periodoDe,
} from '../../utils/syncFormat';

/**
 * Detalle de UNA sincronización: el proceso exacto que siguió.
 *
 * Recupera —y mejora— la consola que existía cuando el navegador conducía el
 * proceso. Aquella narración se perdía al cerrar la pestaña; esta la escribe el
 * servidor y queda guardada con la solicitud, así que se puede abrir meses
 * después y ver el camino completo, incluidos los tropiezos que se superaron.
 */

const ICONO = { ok: 'check_circle', error: 'error', aviso: 'warning', info: 'radio_button_unchecked' };
const COLOR = { ok: 'var(--ok)', error: 'var(--danger)', aviso: 'var(--warn)', info: 'var(--muted)' };

// 24 h a propósito: es un registro de proceso y se lee en columna. Con el
// formato de es-MX salía "01:35:03 p. m.", que ocupa más y se compara peor.
const Dato = ({ etiqueta, children }) => (
  <div>
    <div className="a2-tcell-muted">{etiqueta}</div>
    <div className="a2-tcell" style={{ fontWeight: 600, color: 'var(--heading)' }}>{children}</div>
  </div>
);

const SyncDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [solicitud, setSolicitud] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const temporizador = useRef(null);

  usePageTitle({ title: 'Detalle de la sincronización', kicker: 'Operación' });

  const cargar = useCallback(async () => {
    try {
      const r = await strapiService.satSync.detalle(id);
      setSolicitud(r?.data || null);
      setError(null);
    } catch (e) {
      console.error('Error cargando el detalle:', e);
      setError('No se pudo cargar esta sincronización.');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras siga viva, se refresca sola: es la pantalla donde se mira el avance.
  const vivo = esVivo(solicitud?.status);
  useEffect(() => {
    clearInterval(temporizador.current);
    if (!vivo) return undefined;
    temporizador.current = setInterval(cargar, 5000);
    return () => clearInterval(temporizador.current);
  }, [vivo, cargar]);

  if (cargando) return <SkeletonPage kpis={3} rows={6} />;

  if (error || !solicitud) {
    return (
      <EmptyState
        icon="error"
        size="lg"
        title="No se encontró la sincronización"
        text={error || 'Puede que se haya eliminado.'}
        action={<Button variant="outline" icon="arrow_back" onClick={() => navigate('/app/sync')}>Volver</Button>}
      />
    );
  }

  const eventos = Array.isArray(solicitud?.metadata?.eventos) ? solicitud.metadata.eventos : [];
  const empresa = solicitud?.company?.name || 'Empresa';
  const paquetes = Array.isArray(solicitud?.packages) ? solicitud.packages : [];
  const terminada = solicitud.status === 'terminada';
  // Solo las sincronizaciones de jul-2026 en adelante guardaron cuántos CFDI
  // entregó el SAT y cuántos ya estaban; de las viejas solo consta lo que entró.
  const conDesglose = (solicitud.sat_cfdis_reported ?? 0) > 0;

  return (
    <div className="a2-anim-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" icon="arrow_back" onClick={() => navigate('/app/sync')}>
          Sincronizaciones
        </Button>
        <span className="a2-tcell-muted">·</span>
        <span style={{ font: "700 1rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{empresa}</span>
        <EstadoSync status={solicitud.status} />
      </div>

      {/* Qué trajo: la pregunta que todos hacen al ver "terminada".
          Las solicitudes anteriores a jul-2026 no guardaron el desglose, y
          pintarlo igual diría "el SAT entregó 0" junto a "117 nuevos": una
          cifra que nadie midió. De esas se muestra solo lo que sí consta. */}
      {terminada ? (
        conDesglose ? (
          <div className="a2-grid-kpi">
            <KpiCard icon="inventory_2" label="Recibos que entregó el SAT" value={solicitud.sat_cfdis_reported} />
            <KpiCard icon="add_circle" label="Nuevos en el sistema" value={solicitud.receipts_new ?? 0} />
            <KpiCard icon="done_all" label="Ya los tenías" value={solicitud.receipts_existing ?? 0} />
            {(solicitud.receipts_errors ?? 0) > 0 ? (
              <KpiCard icon="report" label="No se pudieron leer" value={solicitud.receipts_errors} />
            ) : null}
          </div>
        ) : (
          <div className="a2-grid-kpi">
            <KpiCard icon="add_circle" label="Recibos nuevos en el sistema" value={solicitud.receipts_new ?? 0} />
            <KpiCard icon="inventory_2" label="Paquetes que entregó el SAT" value={paquetes.length} />
          </div>
        )
      ) : null}

      <Card pad={false} style={{ padding: 'clamp(18px,2.5vw,24px)' }}>
        <SyncStatusCard solicitud={solicitud} vivo={vivo} siempreFases />
      </Card>

      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>Datos de la solicitud</h3>
        <div
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,180px), 1fr))',
            gap: 14, marginTop: 14,
          }}
        >
          <Dato etiqueta="Periodo pedido al SAT">
            <span className="a2-mono">{periodoDe(solicitud)}</span>
          </Dato>
          <Dato etiqueta="Solicitada">{fechaHora(solicitud.started_at, { mesLargo: true })}</Dato>
          <Dato etiqueta="Terminada">{fechaHora(solicitud.completed_at, { mesLargo: true })}</Dato>
          <Dato etiqueta="Duración">{duracion(solicitud.started_at, solicitud.completed_at)}</Dato>
          <Dato etiqueta="Origen">{ORIGEN[solicitud.origin] || '—'}</Dato>
          <Dato etiqueta="Quién la pidió">
            {solicitud.triggered_by
              ? `${solicitud.triggered_by.first_name || ''} ${solicitud.triggered_by.last_name || ''}`.trim()
                || solicitud.triggered_by.email
              : 'El sistema'}
          </Dato>
          {solicitud.request_id ? (
            <Dato etiqueta="Folio del SAT">
              <span className="a2-mono" style={{ fontSize: '.78rem' }}>{solicitud.request_id}</span>
            </Dato>
          ) : null}
          <Dato etiqueta="Paquetes">{paquetes.length}</Dato>
        </div>
      </Card>

      {/* La consola, ahora escrita por el servidor y guardada */}
      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>Bitácora del proceso</h3>
        <p className="a2-card-sub" style={{ marginBottom: 14 }}>
          Cada paso que dio el servidor, con su hora. Queda guardado: se puede consultar cuando sea.
        </p>

        {eventos.length === 0 ? (
          <Note tone="info" icon="history">
            <span>
              Esta sincronización es anterior a la bitácora, así que no dejó registro paso a paso.
              Las nuevas sí lo guardan.
            </span>
          </Note>
        ) : (
          <div style={{ display: 'grid', gap: 2 }}>
            {eventos.map((e, i) => (
              <div
                key={`${e.at}-${i}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
                  borderRadius: 9, background: i % 2 ? 'transparent' : 'var(--surface-2)',
                }}
              >
                <Sym
                  name={ICONO[e.tipo] || ICONO.info}
                  size={17}
                  style={{ color: COLOR[e.tipo] || COLOR.info, flexShrink: 0, marginTop: 1 }}
                />
                <span className="a2-mono" style={{ fontSize: '.74rem', color: 'var(--faint)', flexShrink: 0, marginTop: 2 }}>
                  {hora(e.at)}
                </span>
                <span style={{ font: "500 .88rem/1.45 'Hanken Grotesk'", color: 'var(--text)', minWidth: 0 }}>
                  {e.mensaje}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {paquetes.length > 0 ? (
        <Card>
          <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>Paquetes del SAT</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, marginTop: 12 }}>
            {paquetes.map((p) => (
              <div key={p.id} className="a2-trow" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <span className="a2-tcell-strong a2-truncate a2-mono" style={{ display: 'block', fontSize: '.8rem' }}>
                    {p.package_id}
                  </span>
                  <span className="a2-tcell-muted" style={{ display: 'block' }}>
                    {p.file_size ? `${Math.round(Number(p.file_size) / 1024)} KB` : 'sin tamaño'}
                    {p.downloaded_at ? ` · bajado ${hora(p.downloaded_at)}` : ''}
                  </span>
                </span>
                <span className="a2-tcell" style={{ textAlign: 'right' }}>
                  <span className="a2-mono" style={{ fontWeight: 700, color: 'var(--heading)' }}>
                    {p.cfdis_in_package || p.extracted_receipts_count || 0}
                  </span>
                  <span className="a2-tcell-muted"> CFDI</span>
                  {p.cfdis_in_package ? (
                    <span className="a2-tcell-muted" style={{ display: 'block' }}>
                      {p.receipts_new ?? 0} nuevos · {p.receipts_existing ?? 0} ya estaban
                      {(p.receipts_errors ?? 0) > 0 ? ` · ${p.receipts_errors} con error` : ''}
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
};

export default SyncDetalle;
